import { Directive, ViewContainerRef, Input, Host, Optional, Self } from "@angular/core";
import { DOM_HANDLER_MODE_TOKEN, DomHandlerMode, DomHandlerService } from "@bfx/primeng/dom";
import { NativeDesktopSupport } from "./native-desktop-support";
import { BfxAutoCompleteComponent } from "../bfx-element-library/bfx-autocomplete.component";

import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { NativeDesktopCalendar } from "./native-desktop-calendar";
import { OELoggerService } from "../order-entry/services/basic/oe-logger.service";

import { LoggerService } from "@bfx/logger";
import { NativeDesktopWindow } from "../../shared/native-desktop/NativeDesktopWindow";
import { MonitorInfo, Rect } from "shared/native-desktop/NativeDesktopData";
import { WindowType } from "shared/native-desktop/NativeDesktopWindowOptions";

const logger = LoggerService.for(__dirname);

/** Class name of body of dropdown containing selection */
const DROPDOWN_ITEM_LIST_CLASS = "bfx-dropdown-item-container";

/** Class added to all body elements of dropdown windows */
export const NATIVE_DROPDOWN_CLASS: string = "bfx-native-dropdown";
@Directive({
  selector: "[nativeDesktopDropdown]",
  providers: [
    {
      provide: DOM_HANDLER_MODE_TOKEN,
      useFactory: () => {
        if (NativeDesktopSupport.isNativeDesktopContainer())
          return new DomHandlerService(DomHandlerMode.NativeContainerEffectsOnly);
        return new DomHandlerService(DomHandlerMode.Browser);
      },
    },
  ],
  standalone: false,
})
export class NativeDesktopDropdown {
  private static _dropdownWindow: NativeDesktopWindow;
  private static _activeComponent: BfxAutoCompleteComponent;
  private static _initWindowPromise: Promise<void>;

  public static get activeComponent(): BfxAutoCompleteComponent {
    return NativeDesktopDropdown._activeComponent;
  }

  public static set activeComponent(value: BfxAutoCompleteComponent) {
    if (value == null && NativeDesktopDropdown._activeComponent != null) {
      let autocomplete = NativeDesktopDropdown._activeComponent;
      NativeDesktopDropdown.reattachPanel(autocomplete);
    }
    NativeDesktopDropdown._activeComponent = value;
  }
  private static _subscription: Subscription;
  private _screenBounds: Rect;
  private _activeWindow: boolean = false;
  private _groupName: string;
  private _ac: AbortController;

  @Input("nativeDesktopDropdown")
  public target: string;
  static _dropdownWindowName: any;

  constructor(
    @Host() @Self() @Optional() component: BfxAutoCompleteComponent,
    protected oeLogger: OELoggerService,
  ) {
    this.oeLogger = this.oeLogger.for(__dirname);
    if (
      NativeDesktopSupport.isNativeDesktopContainer() &&
      NativeDesktopSupport.enableDropdowns === true
    ) {
      this.initialize(component);
    }
  }

  private initialize(component: BfxAutoCompleteComponent): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (component instanceof BfxAutoCompleteComponent) {
        let autocomplete: BfxAutoCompleteComponent = component as BfxAutoCompleteComponent;
        // Disabled for FXBFXOM-327
        // autocomplete.checkDirtyBlur = true;
        Promise.allSettled([
          autocomplete.afterViewInitReady,
          NativeDesktopDropdown.initWindow(),
        ]).then(() => {
          NativeDesktopDropdown.reattachPanel(autocomplete);
          if (
            autocomplete.skipNativeBlurFormatting === false &&
            autocomplete.useAlternateBlur === false
          ) {
            logger.out.warn(
              `${this.constructor.name}.initialized(%O) used on autocomplete with incorrect config. item clicks may not register`,
            );
          }
        });
        autocomplete.onShowDropdown.subscribe(this.onShowDropdown.bind(this, autocomplete));
        autocomplete.onHideDropdown.subscribe(this.onHideDropdown.bind(this, autocomplete));
      }

      let monitorInfo = NativeDesktopSupport.getMonitorInfo();
      this._screenBounds = monitorInfo.primaryMonitor.monitorRect;
    }
  }

  public onShowDropdown(autocomplete: BfxAutoCompleteComponent): void {
    NativeDesktopDropdown._activeComponent = autocomplete;
    //
    NativeDesktopDropdown.reattachPanel(autocomplete);
    let parentWindow = NativeDesktopSupport.getWindow(autocomplete.inputEL.nativeElement);

    // parentWindow.name is from the dropdown window name
    let dropdownWindow = NativeDesktopDropdown._dropdownWindow;
    this.clearDropdown(dropdownWindow);

    dropdownWindow.setParentWindow(parentWindow);

    const dropdownWindowName = NativeDesktopDropdown._dropdownWindowName;

    if (this._activeWindow == true) {
      this._groupName = dropdownWindowName;
      // parent should have already registered to  manage add/delete
      // NativeDesktopSupport.addActiveName(parentWindow.name);
    }

    let dropdownElement: Element = autocomplete.panelEL.nativeElement;

    // if parentWindow is calendar, fyi if not => parentWindow is order entry
    if (
      NativeDesktopCalendar._calendarWindow != null &&
      parentWindow["name"] === NativeDesktopCalendar._calendarWindow?.getId()
    ) {
      NativeDesktopCalendar._calendarDropdownOpen = true;
    }

    setTimeout(() => {
      this.adjustWindow(dropdownWindow, dropdownElement, autocomplete);
    }, 0);
    if (NativeDesktopDropdown._subscription != null) {
      NativeDesktopDropdown._subscription.unsubscribe();
    }

    NativeDesktopDropdown._subscription = autocomplete.onSuggestionsChanged
      .pipe(debounceTime(100))
      .subscribe(() => {
        this.adjustPanel(dropdownWindow, dropdownElement, autocomplete);
      });
  }

  /**
   * Intent is to remove all nodes from Body - created as we were getting artifacts from a previous
   * dropdown when displaying a new one but this didn't really have any desired affect
   * @param dropdownWindow
   */
  private clearDropdown(dropdownWindow: NativeDesktopWindow) {
    const doc = dropdownWindow?.getNativeWindow()?.document;
    if (doc) {
      let bodyElement: Node = doc.body;
      while (bodyElement?.firstChild) {
        bodyElement.removeChild(bodyElement.firstChild);
      }
    }
  }

  private adjustWindow(
    dropdownWindow: NativeDesktopWindow,
    dropdownElement: Element,
    autocomplete: BfxAutoCompleteComponent,
  ): void {
    let autocompleteInput = autocomplete.inputEL.nativeElement;

    let parentWindow = NativeDesktopSupport.getWindow(autocompleteInput);

    let { width, height } = this.calculateDropdownSize(autocomplete, dropdownElement);

    let bounds: DOMRect = autocompleteInput.parentElement.parentElement.getBoundingClientRect();
    let dropdownDimensions = dropdownElement.getBoundingClientRect();
    let wnd: Window = parentWindow.getNativeWindow();

    let x: number = wnd.screenX + bounds.left * NativeDesktopSupport.zoomLevel;
    let y: number = 0; //wnd.screenY + (bounds.top + bounds.height) * NativeDesktopSupport.zoomLevel;

    if (x + width > wnd.screenLeft + wnd.screen.availWidth) {
      x = wnd.screenLeft + wnd.screen.availWidth - width;
    }
    if (y + height > wnd.screenTop + wnd.screen.availHeight) {
      y = wnd.screenTop + wnd.screenY + bounds.top - height;
    }

    if (wnd.screenY + dropdownDimensions.bottom > this._screenBounds.bottom) {
      y = wnd.screenY + (bounds.top - dropdownDimensions.height) * NativeDesktopSupport.zoomLevel;
    } else {
      y = wnd.screenY + (bounds.top + bounds.height) * NativeDesktopSupport.zoomLevel;
    }

    if (dropdownElement["offsetParent"] === null) {
      console.warn(
        `${this.constructor.name}.adjustWindow() failed re-attaching lost panel ${
          dropdownElement["offsetParent"] === null
        }`,
      );
      this.onHideDropdown();
      return;
    }
    dropdownElement["style"].left = dropdownElement["style"].top = "0px";
    autocomplete["handleAlignPanel"] = () => {};

    dropdownWindow.setBounds(x, y, width, height).then(() => {
      let bodyElement: Node = dropdownWindow.getNativeWindow().document.body;
      let bodyClone: Node = bodyElement.cloneNode(false);
      bodyClone.appendChild(dropdownElement);
      if (bodyClone["classList"]) {
        (bodyClone as HTMLElement).classList.add(NATIVE_DROPDOWN_CLASS);
      }
      bodyElement.parentElement.replaceChild(bodyClone, bodyElement);
      dropdownElement["style"].visibility = "visible";
      let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(autocomplete.el.nativeElement);
      if (wnd != null) {
        dropdownWindow.show().then(() => {
          // TODO ELECTRON
          // let parentWindowRef = fin.Window.wrapSync({
          //   uuid: parentWindow.uuid,
          //   name: parentWindow.name,
          // });
          // parentWindowRef["prependOnceListener"](
          //   "bounds-changed",
          //   this.onHideDropdown.bind(this),
          // );
          if (dropdownElement["offsetParent"] === null) {
            this.onHideDropdown();
          }
          autocomplete.highlightOptionChanged();
        });
      }
    });
  }

  private adjustPanel(
    dropdownWindow: NativeDesktopWindow,
    dropdownElement: Element,
    autocomplete: BfxAutoCompleteComponent,
  ): void {
    let { width, height } = this.calculateDropdownSize(autocomplete, dropdownElement);

    dropdownElement["style"].top = "0px";
    dropdownElement["style"].left = "0px";

    if (width > 0 && height > 0) {
      dropdownWindow.setSize(width, height).then(() => {
        let parentWindow: NativeDesktopWindow = NativeDesktopSupport.getWindow(
          autocomplete.inputEL.nativeElement,
        );

        if (parentWindow == null) {
          return;
        }

        if (dropdownElement["offsetParent"] === null) {
          this.onHideDropdown();
        }

        let wnd: Window = parentWindow.getNativeWindow();
        let dropdownDimensions = dropdownElement.getBoundingClientRect();
        let bounds: DOMRect =
          autocomplete.inputEL.nativeElement.parentElement.parentElement.getBoundingClientRect();

        let x: number = wnd.screenX + bounds.left * NativeDesktopSupport.zoomLevel;
        let y: number = 0; //\wnd.screenY + (bounds.top +  bounds.height) * NativeDesktopSupport.zoomLevel;

        if (x + width > wnd.screenLeft + wnd.screen.availWidth) {
          x = wnd.screenLeft + wnd.screen.availWidth - width;
        }
        if (y + height > wnd.screenTop + wnd.screen.availHeight) {
          y = wnd.screenTop + wnd.screenY + bounds.top - height;
        }

        //logger.out.info('Panel sizes: ', wnd.screenY, ' ', wnd.outerHeight, ' ', dropdownDimensions.bottom, ' ', this._screenBounds.bottom);

        if (wnd.screenY + wnd.outerHeight + dropdownDimensions.bottom > this._screenBounds.bottom) {
          y =
            wnd.screenY + (bounds.top - dropdownDimensions.height) * NativeDesktopSupport.zoomLevel;
        } else {
          y = wnd.screenY + (bounds.top + bounds.height) * NativeDesktopSupport.zoomLevel;
        }

        this.outputExtendedLogging(
          `DESKTOP: DROPDOWN.adjustPanel(): ADJUST => dropdownWindow: ${dropdownWindow?.getId()} | ` +
            `parentWindow: ${parentWindow.getId()}` +
            ` | bounds = [x: ${x}, y: ${y}, width: ${width}, height ${height}]`,
        );

        dropdownWindow.setBounds(x, y, width, height).then(() => {
          dropdownWindow.show().then(() => {
            if (dropdownElement["offsetParent"] === null) {
              this.onHideDropdown();
            }
            autocomplete.highlightOptionChanged();
          });
        });
      });
    }
  }

  /**
   * Determine the correct size of the srop down from component and HTML
   * @param autocomplete
   * @param dropdownElement
   * @returns
   */
  private calculateDropdownSize(autocomplete: BfxAutoCompleteComponent, dropdownElement: Element) {
    let width: number = 0;
    let height: number = 0;
    if (dropdownElement && autocomplete) {
      const sizingElement: DOMRect = dropdownElement.getBoundingClientRect();

      width = sizingElement.width * NativeDesktopSupport.zoomLevel;
      height = sizingElement.height * NativeDesktopSupport.zoomLevel;

      // Allow addtional pixels for border width assume border of 1px
      width += 2;

      if (height == 0) {
        logger.out.error(
          `${this.constructor.name}.calculateDropdownSize() failed to calculate height`,
        );
        Math.min(24, autocomplete.suggestions?.length * 16 + 8) * NativeDesktopSupport.zoomLevel;
      }
    } else {
      console.warn(`${this.constructor.name}.calculateDropdownSize()`);
    }
    return { width, height };
  }

  public onHideDropdown(eventAutocomplete?: BfxAutoCompleteComponent): void {
    let autocomplete: BfxAutoCompleteComponent = NativeDesktopDropdown._activeComponent;

    if (
      eventAutocomplete != null &&
      eventAutocomplete instanceof BfxAutoCompleteComponent &&
      (autocomplete == null ||
        (autocomplete instanceof BfxAutoCompleteComponent &&
          autocomplete.target !== eventAutocomplete.target))
    ) {
    } else {
      if (NativeDesktopDropdown._subscription != null) {
        NativeDesktopDropdown._subscription.unsubscribe();
        NativeDesktopDropdown._subscription = null;
      }

      if (NativeDesktopDropdown._dropdownWindow != null) {
        if (autocomplete != null) {
          autocomplete.panelVisible = false;
        }
        this.clearDropdown(NativeDesktopDropdown._dropdownWindow);
        NativeDesktopDropdown._dropdownWindow.hide();
      }

      if (autocomplete != null) {
        NativeDesktopDropdown.reattachPanel(autocomplete);
        NativeDesktopDropdown._activeComponent = null;
      }
    }
  }

  public static initWindow(): Promise<void> {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      if (NativeDesktopDropdown._dropdownWindow != null) {
        return Promise.resolve();
      }

      if (this._initWindowPromise == null) {
        this._initWindowPromise = new Promise<void>((res) =>
          NativeDesktopSupport.openWindow(
            "dropdown",
            WindowType.Dropdown,
            false,
            null,
            (dropdownWindow: NativeDesktopWindow) => {
              NativeDesktopDropdown._dropdownWindow = dropdownWindow;
              NativeDesktopDropdown._dropdownWindowName = dropdownWindow.getId();
              res();
            },
          ),
        );
      }

      return this._initWindowPromise;
    }

    return Promise.resolve();
  }

  private static reattachPanel(autocomplete: BfxAutoCompleteComponent) {
    try {
      autocomplete.appendTo = null;
    } catch (e) {
      logger.out.error(`${this.constructor.name}.reattachPanel() 1:`);
      logger.out.error(e);
      if (autocomplete != null || autocomplete instanceof BfxAutoCompleteComponent == false) {
        logger.out.error(
          `${this.constructor.name}.reattachPanel() invalid autocomplete %o`,
          autocomplete,
        );
        return;
      }
    }

    try {
      autocomplete.panelEL.nativeElement.attributeStyleMap.delete("opacity");
      autocomplete.panelEL.nativeElement.style.visibility = "hidden";
      autocomplete.panelEL.nativeElement.style.isolation = "isolate";
    } catch (e) {
      logger.out.error(`${this.constructor.name}.reattachPanel() 2: %o`, autocomplete);
      logger.out.error(e);
    }

    try {
      autocomplete.panelParentEL.nativeElement.appendChild(autocomplete.panelEL.nativeElement);
    } catch (e) {
      logger.out.error(`${this.constructor.name}.reattachPanel() 3: %o`, autocomplete);
      logger.out.error(e);
    }

    try {
      void autocomplete.el.nativeElement.children[0].offsetHeight;
      void autocomplete.panelEL.nativeElement.offsetHeight;
    } catch (e) {
      logger.out.error(`${this.constructor.name}.reattachPanel() 4: %o`, autocomplete);
      logger.out.error(e);
    }

    try {
      autocomplete.el.nativeElement.removeAttribute("appendTo");
    } catch (e) {
      logger.out.error(`${this.constructor.name}.reattachPanel() 5: %o`, autocomplete);
      logger.out.error(e);
    }
  }

  /**
   * select autocomplete for detail-fill or grids. blur autocomplete for oe
   *
   * do nothing if a search is in progress (processing user input)
   *
   * @param autocomplete
   * @returns
   */
  private static reselectParentWindow(autocomplete: BfxAutoCompleteComponent, window): () => void {
    return () => {
      if (autocomplete.focusInputOnSelect) {
        autocomplete.focusInput();
      } else if (autocomplete.timeout != null && autocomplete.loading == false) {
        autocomplete.blurInput();
      } else {
        window.getNativeWindow().focus();
      }
    };
  }

  @Input("nativeDesktopActiveWindow")
  public set nativeDesktopActiveWindow(value) {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      this.outputExtendedLogging(`DESKTOP: enabling parent small window mode for ${value}`);
      this._activeWindow = true;
    }
  }

  /**********************************************************************/
  /* HELPERS - Debug Log - Extended logging  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputRateLogging(value, dump);
  }
}
